/**
 * IndexedDB utility for offline data storage
 * Stores bucket items, user data, and pending changes for sync
 */

const DB_NAME = 'duolyst-offline';
const DB_VERSION = 1;

const STORES = {
  ITEMS: 'bucket_items',
  USER: 'user_data',
  PENDING_SYNC: 'pending_sync',
  PHOTOS: 'photos',
  COMMENTS: 'comments',
};

let db = null;

export const initDB = () => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('Failed to open IndexedDB:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      console.log('IndexedDB initialized successfully');
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;

      // Bucket items store
      if (!database.objectStoreNames.contains(STORES.ITEMS)) {
        const itemsStore = database.createObjectStore(STORES.ITEMS, { keyPath: 'id' });
        itemsStore.createIndex('created_by', 'created_by', { unique: false });
        itemsStore.createIndex('completed', 'completed', { unique: false });
        itemsStore.createIndex('updated_at', 'updated_at', { unique: false });
      }

      // User data store
      if (!database.objectStoreNames.contains(STORES.USER)) {
        database.createObjectStore(STORES.USER, { keyPath: 'key' });
      }

      // Pending sync store (for offline changes)
      if (!database.objectStoreNames.contains(STORES.PENDING_SYNC)) {
        const syncStore = database.createObjectStore(STORES.PENDING_SYNC, { keyPath: 'id', autoIncrement: true });
        syncStore.createIndex('type', 'type', { unique: false });
        syncStore.createIndex('timestamp', 'timestamp', { unique: false });
      }

      // Photos store
      if (!database.objectStoreNames.contains(STORES.PHOTOS)) {
        const photosStore = database.createObjectStore(STORES.PHOTOS, { keyPath: 'id' });
        photosStore.createIndex('item_id', 'item_id', { unique: false });
      }

      // Comments store
      if (!database.objectStoreNames.contains(STORES.COMMENTS)) {
        const commentsStore = database.createObjectStore(STORES.COMMENTS, { keyPath: 'id' });
        commentsStore.createIndex('item_id', 'item_id', { unique: false });
      }
    };
  });
};

// Generic store operations
const getStore = (storeName, mode = 'readonly') => {
  if (!db) throw new Error('Database not initialized');
  const transaction = db.transaction(storeName, mode);
  return transaction.objectStore(storeName);
};

// ===== BUCKET ITEMS =====

export const saveItemsOffline = async (items) => {
  await initDB();
  const store = getStore(STORES.ITEMS, 'readwrite');
  
  return new Promise((resolve, reject) => {
    const transaction = store.transaction;
    
    items.forEach(item => {
      store.put({ ...item, _offlineUpdated: new Date().toISOString() });
    });
    
    transaction.oncomplete = () => resolve(true);
    transaction.onerror = () => reject(transaction.error);
  });
};

export const getItemsOffline = async () => {
  await initDB();
  const store = getStore(STORES.ITEMS);
  
  return new Promise((resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
};

export const saveItemOffline = async (item) => {
  await initDB();
  const store = getStore(STORES.ITEMS, 'readwrite');
  
  return new Promise((resolve, reject) => {
    const request = store.put({ ...item, _offlineUpdated: new Date().toISOString() });
    request.onsuccess = () => resolve(true);
    request.onerror = () => reject(request.error);
  });
};

export const deleteItemOffline = async (itemId) => {
  await initDB();
  const store = getStore(STORES.ITEMS, 'readwrite');
  
  return new Promise((resolve, reject) => {
    const request = store.delete(itemId);
    request.onsuccess = () => resolve(true);
    request.onerror = () => reject(request.error);
  });
};

// ===== USER DATA =====

export const saveUserOffline = async (user) => {
  await initDB();
  const store = getStore(STORES.USER, 'readwrite');
  
  return new Promise((resolve, reject) => {
    const request = store.put({ key: 'current_user', ...user });
    request.onsuccess = () => resolve(true);
    request.onerror = () => reject(request.error);
  });
};

export const getUserOffline = async () => {
  await initDB();
  const store = getStore(STORES.USER);
  
  return new Promise((resolve, reject) => {
    const request = store.get('current_user');
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

// ===== PENDING SYNC QUEUE =====

export const addPendingSync = async (action) => {
  await initDB();
  const store = getStore(STORES.PENDING_SYNC, 'readwrite');
  
  const syncItem = {
    ...action,
    timestamp: new Date().toISOString(),
    retries: 0,
  };
  
  return new Promise((resolve, reject) => {
    const request = store.add(syncItem);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

export const getPendingSync = async () => {
  await initDB();
  const store = getStore(STORES.PENDING_SYNC);
  
  return new Promise((resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => {
      // Sort by timestamp (oldest first)
      const items = request.result || [];
      items.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      resolve(items);
    };
    request.onerror = () => reject(request.error);
  });
};

export const removePendingSync = async (id) => {
  await initDB();
  const store = getStore(STORES.PENDING_SYNC, 'readwrite');
  
  return new Promise((resolve, reject) => {
    const request = store.delete(id);
    request.onsuccess = () => resolve(true);
    request.onerror = () => reject(request.error);
  });
};

export const clearPendingSync = async () => {
  await initDB();
  const store = getStore(STORES.PENDING_SYNC, 'readwrite');
  
  return new Promise((resolve, reject) => {
    const request = store.clear();
    request.onsuccess = () => resolve(true);
    request.onerror = () => reject(request.error);
  });
};

// ===== PHOTOS =====

export const savePhotosOffline = async (photos) => {
  await initDB();
  const store = getStore(STORES.PHOTOS, 'readwrite');
  
  return new Promise((resolve, reject) => {
    const transaction = store.transaction;
    photos.forEach(photo => store.put(photo));
    transaction.oncomplete = () => resolve(true);
    transaction.onerror = () => reject(transaction.error);
  });
};

export const getPhotosOffline = async (itemId) => {
  await initDB();
  const store = getStore(STORES.PHOTOS);
  const index = store.index('item_id');
  
  return new Promise((resolve, reject) => {
    const request = index.getAll(itemId);
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
};

// ===== COMMENTS =====

export const saveCommentsOffline = async (comments) => {
  await initDB();
  const store = getStore(STORES.COMMENTS, 'readwrite');
  
  return new Promise((resolve, reject) => {
    const transaction = store.transaction;
    comments.forEach(comment => store.put(comment));
    transaction.oncomplete = () => resolve(true);
    transaction.onerror = () => reject(transaction.error);
  });
};

export const getCommentsOffline = async (itemId) => {
  await initDB();
  const store = getStore(STORES.COMMENTS);
  const index = store.index('item_id');
  
  return new Promise((resolve, reject) => {
    const request = index.getAll(itemId);
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
};

// ===== CLEAR ALL =====

export const clearAllOfflineData = async () => {
  await initDB();
  
  const promises = Object.values(STORES).map(storeName => {
    return new Promise((resolve, reject) => {
      const store = getStore(storeName, 'readwrite');
      const request = store.clear();
      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  });
  
  return Promise.all(promises);
};

export default {
  initDB,
  saveItemsOffline,
  getItemsOffline,
  saveItemOffline,
  deleteItemOffline,
  saveUserOffline,
  getUserOffline,
  addPendingSync,
  getPendingSync,
  removePendingSync,
  clearPendingSync,
  savePhotosOffline,
  getPhotosOffline,
  saveCommentsOffline,
  getCommentsOffline,
  clearAllOfflineData,
};
